import { useEffect, useMemo, useRef } from 'react';
import FormattedFraction from 'I18n/components/FormattedFraction';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedPercentage from 'I18n/components/FormattedPercentage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { ArtificialIntelligenceEnhancedIcon } from 'foundations-assets/icons/components/ArtificialIntelligenceEnhancedIcon';
import { KnowledgeBaseIcon } from 'foundations-assets/icons/components/KnowledgeBaseIcon';
import { ReportingAndDataIcon } from 'foundations-assets/icons/components/ReportingAndDataIcon';
import { TimeIcon } from 'foundations-assets/icons/components/TimeIcon';
import { TrophyIcon } from 'foundations-assets/icons/components/TrophyIcon';
import { ViewIcon } from 'foundations-assets/icons/components/ViewIcon';
import { Button } from 'foundations-components/transitional/components/Button';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import UsageTracker from '../../../utils/UsageTracker';
import { areAllAssessedVideosInFocus, showPreAssessmentFocusRibbon } from '../utils/preAssessmentQuizUtils';
import { isVideoAsset } from '../../tracks/tracksUtils';
import { PRE_ASSESSMENTS_CONTENT_PASS_THRESHOLD } from '../../../constants/PreAssessmentsConstants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const LessonPreQuizComplete = ({
  interactionProps,
  lessonContent,
  onStartLesson,
  progressProps,
  quizState
}) => {
  const {
    correctAnswers,
    totalQuestions,
    perContentScores
  } = quizState;
  const hasTrackedCompletion = useRef(false);
  useEffect(() => {
    if (hasTrackedCompletion.current) {
      return;
    }
    hasTrackedCompletion.current = true;
    UsageTracker.trackStandalone('Content Progress', Object.assign({}, progressProps, {
      action: 'ai-pre-assessment-completed',
      score: correctAnswers / totalQuestions * 100,
      total: totalQuestions
    }));
  }, [progressProps, correctAnswers, totalQuestions]);
  const handleStartLesson = () => {
    UsageTracker.trackStandalone('Learning Center Interaction', Object.assign({}, interactionProps, {
      action: 'ai-pre-assessment-start-lesson-clicked'
    }));
    onStartLesson();
  };
  const percentage = Math.round(correctAnswers / totalQuestions * 100);
  const allVideosInFocus = areAllAssessedVideosInFocus({
    lessonContent,
    preQuizState: quizState
  });
  const showMainCompletionScreen = correctAnswers >= 1 && !allVideosInFocus;
  const {
    focusAreaCount,
    assessedContentCount,
    totalMinutes
  } = useMemo(() => {
    const result = lessonContent.reduce((acc, content) => {
      const score = perContentScores[content.id];
      const hasScore = score && score.total > 0;
      const _assessedContentCount = acc.assessedContentCount + (hasScore ? 1 : 0);
      const _focusAreaCount = acc.focusAreaCount + (hasScore && score.correct / score.total <= PRE_ASSESSMENTS_CONTENT_PASS_THRESHOLD ? 1 : 0);
      const shouldFocus = showPreAssessmentFocusRibbon({
        contentId: content.id,
        lessonContent,
        preQuizState: quizState
      });
      const isSkippableVideo = isVideoAsset(content) && hasScore && !shouldFocus;
      const _totalSeconds = acc.totalSeconds + (isSkippableVideo ? content.duration || 0 : 0);
      return {
        assessedContentCount: _assessedContentCount,
        focusAreaCount: _focusAreaCount,
        totalSeconds: _totalSeconds
      };
    }, {
      focusAreaCount: 0,
      assessedContentCount: 0,
      totalSeconds: 0
    });
    return {
      assessedContentCount: result.assessedContentCount,
      focusAreaCount: result.focusAreaCount,
      totalMinutes: allVideosInFocus ? undefined : Math.round(result.totalSeconds / 60)
    };
  }, [allVideosInFocus, lessonContent, perContentScores, quizState]);
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    "data-test-id": "lesson-pre-quiz-complete",
    flexDirection: "column",
    flexGrow: 1,
    gap: "500",
    maxInlineSize: "100%",
    padding: "700",
    children: [/*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      flexDirection: "column",
      flexGrow: 1,
      gap: "1500",
      justifyContent: "center",
      maxInlineSize: "100%",
      children: [/*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        flexDirection: "column",
        gap: "400",
        maxInlineSize: "100%",
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          flexWrap: "wrap",
          gap: "300",
          justifyContent: "center",
          children: [/*#__PURE__*/_jsx(IconHolder, {
            icon: ArtificialIntelligenceEnhancedIcon,
            size: "700"
          }), /*#__PURE__*/_jsx(Text, {
            typography: "heading-700",
            children: showMainCompletionScreen ? /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.preAssessmentQuiz.completeScreen.passed.title"
            }) : /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.preAssessmentQuiz.completeScreen.needsWork.title"
            })
          })]
        }), /*#__PURE__*/_jsx(Text, {
          textAlign: "center",
          typography: "heading-500",
          children: showMainCompletionScreen ? /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "learningCenter.preAssessmentQuiz.completeScreen.passed.score",
            options: {
              fraction: /*#__PURE__*/_jsx(FormattedFraction, {
                denominator: totalQuestions,
                numerator: correctAnswers
              }),
              percentage: /*#__PURE__*/_jsx(FormattedPercentage, {
                value: percentage
              })
            }
          }) : /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            flexDirection: "column",
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.preAssessmentQuiz.completeScreen.needsWork.scoreLine1",
              options: {
                correctAnswers,
                totalQuestions
              }
            }), /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.preAssessmentQuiz.completeScreen.needsWork.scoreLine2"
            })]
          })
        })]
      }), /*#__PURE__*/_jsxs(Flex, {
        flexWrap: "wrap",
        gap: "1000",
        justifyContent: "center",
        children: [/*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          flexDirection: "column",
          children: showMainCompletionScreen ? /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(IconHolder, {
              icon: TimeIcon,
              size: "500"
            }), /*#__PURE__*/_jsx(Text, {
              typography: "body-125",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.preAssessmentQuiz.completeScreen.passed.minutesSaved",
                options: {
                  minutes: totalMinutes
                }
              })
            }), /*#__PURE__*/_jsx(Text, {
              typography: "body-125",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.preAssessmentQuiz.completeScreen.passed.timeSaved"
              })
            })]
          }) : /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(IconHolder, {
              icon: ReportingAndDataIcon,
              size: "500"
            }), /*#__PURE__*/_jsx(Text, {
              typography: "body-125",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.preAssessmentQuiz.completeScreen.needsWork.growthPotential"
              })
            }), /*#__PURE__*/_jsx(Text, {
              typography: "body-125",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.preAssessmentQuiz.completeScreen.needsWork.growthPotentialValue"
              })
            })]
          })
        }), /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          flexDirection: "column",
          children: showMainCompletionScreen ? /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(IconHolder, {
              icon: KnowledgeBaseIcon,
              size: "500"
            }), /*#__PURE__*/_jsx(Text, {
              typography: "body-125",
              children: /*#__PURE__*/_jsx(FormattedPercentage, {
                value: percentage
              })
            }), /*#__PURE__*/_jsx(Text, {
              typography: "body-125",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.preAssessmentQuiz.completeScreen.passed.knowledgeScore"
              })
            })]
          }) : /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(IconHolder, {
              icon: KnowledgeBaseIcon,
              size: "500"
            }), /*#__PURE__*/_jsx(Text, {
              typography: "body-125",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.preAssessmentQuiz.completeScreen.needsWork.whatYoullLearn"
              })
            }), /*#__PURE__*/_jsx(Text, {
              typography: "body-125",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.preAssessmentQuiz.completeScreen.needsWork.whatYoullLearnValue"
              })
            })]
          })
        }), /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          flexDirection: "column",
          children: showMainCompletionScreen ? /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(IconHolder, {
              icon: ViewIcon,
              size: "500"
            }), /*#__PURE__*/_jsx(Text, {
              typography: "body-125",
              children: /*#__PURE__*/_jsx(FormattedFraction, {
                denominator: assessedContentCount,
                numerator: focusAreaCount
              })
            }), /*#__PURE__*/_jsx(Text, {
              typography: "body-125",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.preAssessmentQuiz.completeScreen.passed.areasToFocus"
              })
            })]
          }) : /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(IconHolder, {
              icon: TrophyIcon,
              size: "500"
            }), /*#__PURE__*/_jsx(Text, {
              typography: "body-125",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.preAssessmentQuiz.completeScreen.needsWork.outcome"
              })
            }), /*#__PURE__*/_jsx(Text, {
              typography: "body-125",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.preAssessmentQuiz.completeScreen.needsWork.outcomeValue"
              })
            })]
          })
        })]
      })]
    }), /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      flexDirection: "column",
      gap: "600",
      marginBlockStart: "auto",
      children: [/*#__PURE__*/_jsx(Button, {
        borderRadius: "full",
        onClick: handleStartLesson,
        use: "primary",
        children: /*#__PURE__*/_jsx(Text, {
          paddingBlock: "200",
          paddingInline: "1000",
          typography: "heading-100",
          children: showMainCompletionScreen ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.preAssessmentQuiz.completeScreen.passed.startButton"
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.preAssessmentQuiz.completeScreen.needsWork.startButton"
          })
        })
      }), /*#__PURE__*/_jsx(Text, {
        textAlign: "center",
        children: showMainCompletionScreen ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.preAssessmentQuiz.completeScreen.passed.footer"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.preAssessmentQuiz.completeScreen.needsWork.footer"
        })
      })]
    })]
  });
};